// src/components/SectorBenchmarks.js
import React, { useEffect, useState } from 'react';

const METRIC_FORMAT = {
  'Gross Margin': 'pct_raw',
  'Net Margin': 'pct_raw',
  'Operating Margin': 'pct_raw',
  'EBITDA Margin': 'pct_raw',
  'FCF Margin': 'pct_raw',
  'Debt/Equity': 'ratio',
  'Current Ratio': 'ratio',
};

function formatValue(value, metric) {
  if (value == null) return '—';
  const fmt = METRIC_FORMAT[metric] || 'pct_raw';
  if (fmt === 'pct_raw') return `${value.toFixed(1)}%`;  // already in % form from DB
  return value.toFixed(2);
}

const SectorBenchmarks = ({ stockSymbol, companyName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!stockSymbol) return;

    const fetchSector = async () => {
      setLoading(true);
      try {
        const resp = await fetch(
          `https://metrics-8.onrender.com/api/sector/${encodeURIComponent(stockSymbol)}`
        );
        if (resp.ok) {
          setData(await resp.json());
        }
      } catch (e) {
        console.warn('[SectorBenchmarks] fetch error:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchSector();
  }, [stockSymbol]);

  if (!stockSymbol) return null;
  if (loading) return <div className="text-sm text-gray-500">Loading sector data…</div>;
  if (!data) return null;
  if (data.error) {
    return (
      <div className="card p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-2">Sector Comparison</h3>
        <p className="text-sm text-gray-500">{data.error}</p>
      </div>
    );
  }

  const { sector, comparison, peersInSector } = data;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800">Sector Comparison</h3>
        <span className="text-xs bg-brand-50 text-brand-700 px-2 py-1 rounded-full font-medium">
          {sector}
        </span>
      </div>

      {peersInSector > 0 && (
        <p className="text-xs text-gray-500 mb-3">
          Compared against {peersInSector} peer{peersInSector > 1 ? 's' : ''} in {sector}
        </p>
      )}

      {/* Comparison table */}
      <div className="space-y-2">
        {comparison.map((item) => {
          const companyVal = item.company;
          const sectorVal = item.sector;
          const isAbove = item.vs === 'above' || item.vs === 'better';
          const isEqual = item.vs === 'equal';

          return (
            <div key={item.metric} className="flex items-center gap-3">
              {/* Metric label */}
              <div className="w-28 text-xs text-gray-600 truncate">{item.metric}</div>

              {/* Bar comparison */}
              <div className="flex-1 relative">
                <div className="flex items-center gap-1">
                  {/* Company bar */}
                  <div className="flex-1 bg-gray-100 rounded-full h-4 relative overflow-hidden">
                    <div
                      className={`h-full rounded-full ${isEqual ? 'bg-gray-400' : isAbove ? 'bg-green-500' : 'bg-amber-400'}`}
                      style={{
                        width: `${Math.min(Math.abs(companyVal) / Math.max(Math.abs(sectorVal), Math.abs(companyVal), 0.01) * 100, 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Values */}
              <div className="flex items-center gap-2 min-w-[120px] justify-end">
                <span className={`text-xs font-semibold ${isEqual ? 'text-gray-700' : isAbove ? 'text-green-700' : 'text-amber-700'}`}>
                  {formatValue(companyVal, item.metric)}
                </span>
                <span className="text-[10px] text-gray-400">vs</span>
                <span className="text-xs text-gray-500">
                  {formatValue(sectorVal, item.metric)}
                </span>
              </div>

              {/* Indicator */}
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                item.vs === 'above' || item.vs === 'better' ? 'bg-green-100 text-green-700'
                : item.vs === 'equal' ? 'bg-gray-100 text-gray-600'
                : 'bg-amber-100 text-amber-700'
              }`}>
                {item.vs === 'above' ? '↑' : item.vs === 'better' ? '✓' : item.vs === 'equal' ? '=' : '↓'}
              </span>
            </div>
          );
        })}
      </div>

      {comparison.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4">
          Refresh company data to see sector comparison
        </p>
      )}
    </div>
  );
};

export default SectorBenchmarks;
